import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ExternalLink, Video, Clock3, ClipboardList, Wand2, MessageSquare,
  CheckCircle2, ArrowRight, Compass, Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const SECTIONS = [
  { id: "branded-page", label: "Branded page" },
  { id: "field-update", label: "Field Update" },
  { id: "growth-meeting", label: "Growth meeting" },
  { id: "strategy-review", label: "Strategy review" },
  { id: "copy-assist", label: "AI Copy Assist" },
];

const AGENDA = [
  { time: "0:00", mins: "5 min", title: "Welcome and program update", body: "Key updates, new partners, recent wins, page and tool improvements, and the current program priorities." },
  { time: "0:05", mins: "10 min", title: "Compliance and messaging alignment", body: "Approved language, what partners can and cannot say, how to avoid rate promises, and how to refer an opportunity so licensed professionals handle the mortgage." },
  { time: "0:15", mins: "10 min", title: "Monthly marketing theme", body: "The message of the month — e.g. \"Before you lock, check it\" — with ready-to-use social posts, texts, email language, and talking points." },
  { time: "0:25", mins: "10 min", title: "BDM and MLO growth strategy", body: "One practical business-building topic: approaching agents, introducing the program to a brokerage, inviting referral partners, or handing off a lead properly." },
  { time: "0:35", mins: "10 min", title: "Wins, questions, and action steps", body: "Partner success stories, open Q&A, and one or two clear action items for the month ahead." },
];

const REVIEW_DELIVERABLES = [
  "What's working — and what needs to change, said plainly.",
  "Which messaging is actually converting across the network.",
  "How partners are performing — who's active, who's gone quiet.",
  "What tools, assets, or training are still missing.",
  "How the program scales from here, agreed with you before anything ships.",
];

const SectionHead = ({ eyebrow, title, blurb }) => (
  <>
    <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">{eyebrow}</p>
    <h2 className="mt-3 font-heading font-medium text-3xl sm:text-4xl max-w-2xl text-balance">{title}</h2>
    {blurb ? <p className="mt-4 max-w-2xl text-ink-600 leading-relaxed">{blurb}</p> : null}
  </>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.45, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export function SectionNav() {
  return (
    <div className="sticky top-0 z-30 bg-linen/90 backdrop-blur border-b border-linen-border" data-testid="tl-section-nav">
      <div className="container-grc flex items-center gap-2 overflow-x-auto py-3">
        <span className="text-[10px] uppercase tracking-[0.2em] text-ink-400 shrink-0 pr-1">See it</span>
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            data-testid={`tl-jump-${s.id}`}
            className="shrink-0 text-xs font-medium rounded-full border border-linen-border bg-white px-3 py-1.5 text-ink-600 hover:border-teal hover:text-navy transition-colors"
          >
            {s.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function BrandedPageDemo() {
  const [sample, setSample] = useState(null); // null = loading, false = none configured

  useEffect(() => {
    axios
      .get(`${API}/site/walkthrough-sample`)
      .then((r) => setSample(r.data?.slug ? r.data : false))
      .catch(() => setSample(false));
  }, []);

  const slug = sample?.slug;
  const url = slug ? `${window.location.origin}/${slug}` : null;

  return (
    <section id="branded-page" className="section-y scroll-mt-16">
      <div className="container-grc">
        <SectionHead
          eyebrow="See it · Branded page"
          title="Every officer is live at greatratecheck.com/their-name."
          blurb="No design work, no web developer, no waiting. Here's a real one — scroll it, click it, it's the actual page a borrower sees."
        />
        <Reveal className="mt-8">
          <div className="rounded-3xl bg-white border border-linen-border shadow-soft overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-linen-border bg-linen">
              <span className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#E6E2D6]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#E6E2D6]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#E6E2D6]" />
              </span>
              <span className="ml-2 text-xs text-ink-500 font-mono truncate">greatratecheck.com/{slug || "…"}</span>
              <a
                href={url || "#branded-page"}
                target="_blank"
                rel="noreferrer"
                data-testid="tl-demo-open"
                className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-teal hover:text-navy transition-colors shrink-0"
              >
                Open it <ExternalLink size={12} />
              </a>
            </div>
            {sample === false ? (
              <div className="p-10 text-center" data-testid="tl-demo-missing">
                <p className="font-heading text-xl text-navy">A live sample page sits here.</p>
                <p className="mt-2 text-sm text-ink-500 max-w-md mx-auto">
                  Each officer gets their own at greatratecheck.com/their-name, with their photo, bio, NMLS,
                  and lead routing straight to them.
                </p>
              </div>
            ) : !slug ? (
              <div className="h-[420px] sm:h-[520px] bg-linen" />
            ) : (
              <div className="relative h-[420px] sm:h-[520px] overflow-hidden bg-linen flex justify-center" data-testid="tl-demo-frame">
                <div className="relative w-[749px] h-full overflow-hidden">
                  <iframe
                    src={`/${slug}`}
                    title="Sample branded officer page"
                    loading="lazy"
                    className="absolute top-0 left-0 w-[1440px] h-[1600px] origin-top-left border-0 pointer-events-none"
                    style={{ transform: "scale(0.52)" }}
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
              </div>
            )}
          </div>
        </Reveal>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            "Their photo, bio, NMLS and phone — pulled from their profile, never hand-coded.",
            "Leads from that page route to that officer and land in their dashboard.",
            "A matching apply page at /their-name/apply feeds their own application pipeline.",
          ].map((t, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <p className="text-sm text-ink-600 leading-relaxed flex items-start gap-2">
                <CheckCircle2 size={16} className="text-teal shrink-0 mt-0.5" /> {t}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FieldUpdateDemo() {
  return (
    <section id="field-update" className="section-y pt-6 scroll-mt-16">
      <div className="container-grc">
        <SectionHead
          eyebrow="See it · Weekly"
          title="This is a Field Update. Every week, without fail."
          blurb="Four parts, three minutes, one action item. It's the difference between a program your team joined and a program your team uses."
        />
        <Reveal className="mt-8">
          <div className="rounded-3xl bg-white border border-linen-border shadow-soft overflow-hidden max-w-3xl">
            <div className="px-6 py-4 border-b border-linen-border bg-linen">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink-400">Subject line</p>
              <p className="mt-1 font-medium text-navy" data-testid="tl-fu-subject">
                Field Update — "Before you lock, check it"
              </p>
            </div>
            <div className="p-6 space-y-6">
              {/* Video slot — intentionally a placeholder until the first brief is produced */}
              <div className="rounded-2xl bg-navy relative overflow-hidden aspect-video flex items-center justify-center" data-testid="tl-fu-video">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-lime/20 blur-3xl" aria-hidden />
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-[0.07]"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
                    backgroundSize: "44px 44px",
                  }}
                />
                <div className="relative text-center px-6">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/10 border border-white/20 text-lime">
                    <Video size={22} />
                  </span>
                  <p className="mt-3 text-white font-heading text-lg">The 3-minute brief goes here</p>
                  <p className="mt-1 text-xs text-white/60 max-w-sm mx-auto leading-relaxed">
                    Recorded, not live — partners watch it on their own time. Message of the week, delivered
                    the same way every week.
                  </p>
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-teal font-bold">1 · This week's message</p>
                <p className="mt-1.5 text-sm text-ink-600 leading-relaxed">
                  One approved phrase your team can use all week:
                  <span className="text-navy font-medium"> "Before you lock, check it."</span> A rate check
                  costs a borrower nothing and takes about two minutes.
                </p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-teal font-bold">2 · Copy/paste outreach</p>
                <blockquote className="mt-2 rounded-2xl bg-linen border-l-4 border-teal px-5 py-4 text-sm text-navy leading-relaxed italic" data-testid="tl-fu-script">
                  "Quick one — I'm running free rate checks for your buyers this month. If anyone's holding a
                  pre-approval from last year, their number has almost certainly changed. Want me to run a
                  couple and send you the results?"
                </blockquote>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-linen-border p-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-bold">3 · Compliance reminder</p>
                  <p className="mt-1.5 text-sm text-ink-600 leading-relaxed">
                    You're offering a rate check, not quoting a rate. No numbers, no promises — a licensed
                    loan officer handles the mortgage from there.
                  </p>
                </div>
                <div className="rounded-2xl border border-linen-border p-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-bold">4 · This week's action item</p>
                  <p className="mt-1.5 text-sm text-ink-600 leading-relaxed">
                    Send the script above to five agents you've already worked with. Not new ones — five old ones.
                  </p>
                </div>
              </div>

              {/* Written version of the brief — most partners read, they don't press play */}
              <details className="rounded-2xl bg-linen/70 border border-linen-border px-5 py-4" data-testid="tl-fu-transcript">
                <summary className="cursor-pointer text-sm font-medium text-navy marker:text-teal">
                  Prefer to read it? The brief in 90 seconds.
                </summary>
                <div className="mt-3 space-y-3 text-[13px] text-ink-600 leading-relaxed">
                  <p>
                    "This month's message is simple: before you lock, check it. Plenty of buyers are sitting on
                    a pre-approval that's months old, and the number they're planning around isn't the number
                    they'd get today."
                  </p>
                  <p>
                    "Your job isn't to know what their new rate is — it's to offer the check. That's the whole
                    ask. Say it the way it's written above, send it to five agents you already have a
                    relationship with, and let the licensed officer take it from there."
                  </p>
                  <p>
                    "One reminder before you go: don't quote a number, don't promise a savings figure, and
                    don't get pulled into the math on a text thread. Offer the check, hand it off clean."
                  </p>
                </div>
              </details>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function GrowthMeetingDemo() {
  return (
    <section id="growth-meeting" className="section-y pt-6 scroll-mt-16">
      <div className="container-grc">
        <SectionHead
          eyebrow="See it · Monthly"
          title="The Partner Growth Meeting agenda."
          blurb="45 minutes, timed and produced. Not a pep rally, not a webinar nobody watches — a working session your team leaves with something from."
        />
        <Reveal className="mt-8">
          <div className="rounded-3xl bg-white border border-linen-border shadow-soft overflow-hidden max-w-3xl">
            <div className="px-6 py-4 bg-navy text-white flex items-center gap-3">
              <Clock3 size={18} className="text-lime" />
              <div>
                <p className="font-heading text-lg leading-tight">Partner Growth Meeting</p>
                <p className="text-xs text-white/60">45 minutes · live · recorded and posted as a replay</p>
              </div>
            </div>
            <ul className="divide-y divide-linen-border" data-testid="tl-agenda">
              {AGENDA.map((a, i) => (
                <li key={a.time} className="px-6 py-4 flex items-start gap-4" data-testid={`tl-agenda-${i}`}>
                  <span className="shrink-0 w-14 text-sm font-mono text-teal tabular-nums pt-0.5">{a.time}</span>
                  <div className="min-w-0">
                    <p className="font-medium text-navy">
                      {a.title}
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-ink-400">{a.mins}</span>
                    </p>
                    <p className="mt-1 text-sm text-ink-600 leading-relaxed">{a.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="px-6 py-4 bg-linen border-t border-linen-border flex items-start gap-2.5">
              <MessageSquare size={15} className="text-teal shrink-0 mt-0.5" />
              <p className="text-[13px] text-ink-600 leading-relaxed">
                Can't make it? The replay posts the same week and partners get emailed the link — so the
                cadence holds even when calendars don't.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function StrategyReviewDemo() {
  return (
    <section id="strategy-review" className="section-y pt-6 scroll-mt-16">
      <div className="container-grc">
        <SectionHead
          eyebrow="See it · Quarterly"
          title="The Strategy Review — what lands on your desk."
          blurb="This one isn't for your team. It's for you: a leadership-level read on what the program produced and where it goes next."
        />
        <Reveal className="mt-8">
          <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-8 max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-teal-light text-teal">
                <ClipboardList size={20} />
              </span>
              <div>
                <p className="font-heading font-medium text-xl text-navy leading-tight">Quarterly review · 60 minutes</p>
                <p className="text-xs text-ink-400">Packet delivered before the call, so the call is a decision — not a readout</p>
              </div>
            </div>
            <ul className="mt-6 space-y-3" data-testid="tl-review-list">
              {REVIEW_DELIVERABLES.map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-ink-600 leading-relaxed">
                  <Compass size={16} className="text-teal shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CopyAssistDemo() {
  return (
    <section id="copy-assist" className="section-y pt-6 pb-4 scroll-mt-16">
      <div className="container-grc">
        <SectionHead
          eyebrow="See it · AI Copy Assist"
          title="Nobody on your team stares at a blank box."
          blurb="They describe what they want in a sentence. They get on-brand, compliant copy in seconds — ready to send."
        />
        <Reveal className="mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-5 items-center max-w-4xl">
            <div className="rounded-3xl border border-dashed border-linen-border bg-white/60 p-6 h-full">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-bold">Before</p>
              <div className="mt-3 rounded-2xl bg-white border border-linen-border p-4 min-h-[140px] flex items-center justify-center">
                <p className="text-sm text-ink-300 italic">Empty text box. Blinking cursor. Nothing sent.</p>
              </div>
              <p className="mt-3 text-[13px] text-ink-500 leading-relaxed">
                This is where most marketing programs quietly die — not from lack of assets, from lack of words.
              </p>
            </div>

            <div className="hidden lg:flex items-center justify-center">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-lime text-navy-deep">
                <Wand2 size={19} />
              </span>
            </div>

            <div className="rounded-3xl bg-navy text-white p-6 h-full relative overflow-hidden">
              <div className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full bg-teal/25 blur-3xl" aria-hidden />
              <div className="relative">
                <p className="text-[10px] uppercase tracking-[0.2em] text-lime font-bold">After · 8 seconds</p>
                <div className="mt-3 rounded-2xl bg-white/10 border border-white/15 p-4 text-sm leading-relaxed">
                  "Rates moved again this week. If you've got buyers holding a pre-approval from last spring,
                  their numbers have changed — and not always for the worse. Send them my way and I'll run a
                  fresh rate check, no cost and no obligation."
                </div>
                <p className="mt-3 text-[13px] text-white/60 leading-relaxed flex items-start gap-2">
                  <Sparkles size={14} className="text-lime shrink-0 mt-0.5" />
                  On-brand, no rate promises, disclosure-safe — every time.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function DemoTour({ onBook }) {
  return (
    <>
      <BrandedPageDemo />
      <FieldUpdateDemo />
      <GrowthMeetingDemo />
      <StrategyReviewDemo />
      <CopyAssistDemo />
      <section className="pb-14">
        <div className="container-grc">
          <div className="rounded-3xl bg-teal-light/60 border border-teal/20 px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <p className="text-sm text-navy leading-relaxed max-w-xl">
              That's the whole program — the pages, the weekly brief, the monthly meeting, the quarterly
              review, and the tools in between. Nothing here is a mock-up.
            </p>
            <button
              type="button"
              onClick={onBook}
              data-testid="tl-demo-cta"
              className="shrink-0 inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold text-sm px-6 py-3 rounded-full shadow-lift transition-all hover:-translate-y-0.5"
            >
              Book an intro call <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
